'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CustomHeroButton, ResponsiveModal, Typography } from '../../../components';
import OffersTable from './components/OffersTable';
import DepositWarningBanner from './components/DepositWarningBanner';
import FiltersSection from './components/FiltersSection';
import {
  MINIMUM_DEPOSIT,
  DEFAULT_PAGE_SIZE,
  DEFAULT_FILTERS,
  SEARCH_DEBOUNCE_DELAY,
} from './constants';
import clientRevshareService, { RevshareOffer } from '../../../api/client/clientRevshareService';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { useAccount } from '../../../contexts/AccountContext';
import { useNavigate } from 'react-router';
import { useTranslations } from '../../../contexts/translation.context';
import { showError } from '../../../utils/toast';
import { useAuth } from '../../../contexts/AuthContext';
import { OffersFilters } from './types';

const RevshareOffersPage: React.FC = () => {
  const { t } = useTranslations();
  const { user } = useAuth();
  const { accounts } = useAccount();
  const navigate = useNavigate();

  // My offers state
  const [myOffers, setMyOffers] = useState<RevshareOffer[]>([]);
  const [isLoadingMy, setIsLoadingMy] = useState(true);
  const [currentPageMy, setCurrentPageMy] = useState(1);
  const [pageSizeMy, setPageSizeMy] = useState(DEFAULT_PAGE_SIZE);
  const [totalCountMy, setTotalCountMy] = useState(0);
  const [filtersMy, setFiltersMy] = useState<OffersFilters>({ ...DEFAULT_FILTERS });
  const [localSearchMy, setLocalSearchMy] = useState(DEFAULT_FILTERS.search);
  const prevSearchMyRef = useRef(localSearchMy);

  // Other offers state
  const [otherOffers, setOtherOffers] = useState<RevshareOffer[]>([]);
  const [isLoadingOther, setIsLoadingOther] = useState(true);
  const [currentPageOther, setCurrentPageOther] = useState(1);
  const [pageSizeOther, setPageSizeOther] = useState(DEFAULT_PAGE_SIZE);
  const [totalCountOther, setTotalCountOther] = useState(0);
  const [filtersOther, setFiltersOther] = useState<OffersFilters>({ ...DEFAULT_FILTERS });
  const [localSearchOther, setLocalSearchOther] = useState(DEFAULT_FILTERS.search);
  const prevSearchOtherRef = useRef(localSearchOther);

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);

  const breadcrumbs = useMemo(
    () => [
      { label: t('revshare.home'), href: '/' },
      { label: t('revshare.offers.title'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  const hasRequiredDeposit = useMemo(() => {
    return (user?.balance ?? 0) >= MINIMUM_DEPOSIT;
  }, [user?.balance]);

  const hasOnlyFansAccounts = useMemo(() => {
    return accounts && accounts.length > 0;
  }, [accounts]);

  const userId = useMemo(() => user?.id || (user as any)?._id, [user]);

  const hasActiveFiltersMy = useMemo(() => {
    return filtersMy.search !== '' || filtersMy.type !== 'all' || filtersMy.sortBy !== 'all';
  }, [filtersMy]);

  const hasActiveFiltersOther = useMemo(() => {
    return (
      filtersOther.search !== '' || filtersOther.type !== 'all' || filtersOther.sortBy !== 'all'
    );
  }, [filtersOther]);

  // Debounced search for My offers
  useEffect(() => {
    if (prevSearchMyRef.current === localSearchMy) return;

    const timer = setTimeout(() => {
      prevSearchMyRef.current = localSearchMy;
      setFiltersMy((prev) => ({ ...prev, search: localSearchMy }));
      setCurrentPageMy(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearchMy]);

  // Debounced search for Other offers
  useEffect(() => {
    if (prevSearchOtherRef.current === localSearchOther) return;

    const timer = setTimeout(() => {
      prevSearchOtherRef.current = localSearchOther;
      setFiltersOther((prev) => ({ ...prev, search: localSearchOther }));
      setCurrentPageOther(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearchOther]);

  const normalizeOffer = (offer: any): RevshareOffer => {
    const ofData = offer?.of_data || {};
    const offerData = offer?.offer || {};

    const normalizedType = (() => {
      const rawType = (offer?.type || ofData?.type || '').toString().toLowerCase();
      if (rawType === 'free trial' || rawType === 'free_trial') return 'FREE_TRIAL';
      if (rawType === 'free') return 'FREE';
      if (rawType === 'paid') return 'PAID';
      return (offer?.type || ofData?.type || 'FREE') as RevshareOffer['type'];
    })();

    const normalizedStatus = (() => {
      const status = (offerData?.status || '').toString().toUpperCase();
      if (!status) return undefined;
      return status as any;
    })();

    return {
      _id: offer?._id || offer?.id,
      of_user_id: offer?.of_user_id ?? ofData?.of_user_id ?? 0,
      of_user_name: offer?.of_user_name ?? ofData?.of_user_name ?? '',
      name: offer?.name ?? ofData?.name ?? '',
      avatar: offer?.avatar ?? ofData?.avatar,
      subscribers: offer?.subscribers ?? ofData?.subscribers ?? 0,
      overall_popularity_percent:
        offer?.overall_popularity_percent ?? ofData?.overall_popularity_percent ?? 0,
      likes: offer?.likes ?? ofData?.likes ?? 0,
      posts: offer?.posts ?? ofData?.posts ?? 0,
      favorited: offer?.favorited ?? ofData?.favorited ?? 0,
      created_at: offer?.created_at || offer?.createdAt || offerData?.created_at,
      revshare_campaigns_count: offer?.ref_share_active_total ?? offer?.revshareCampaignsCount ?? 0,
      cpl_campaigns_count: offer?.cpl_campaigns_count ?? offer?.cplCampaignsCount ?? 0,
      type: normalizedType,
      status: offer?.status ?? ofData?.status,
      owner: offer?.owner || {
        _id: '',
        name: '',
        type: 'creator',
      },
      priority: offer?.priority,
      of_data: ofData,
      offer: offerData
        ? {
            ...offerData,
            status: normalizedStatus || offerData?.status,
          }
        : undefined,
      track_link: offer?.track_link || offer?.trackLink,
      ref_share_active_total: offer?.ref_share_active_total ?? 0,
      ref_share_transactions_total: offer?.ref_share_transactions_total ?? '0',
      ref_share_subscribers_total: offer?.ref_share_subscribers_total ?? 0,
    } as RevshareOffer;
  };

  // Load My Offers (with owner param)
  const loadMyOffers = useCallback(async () => {
    if (!userId) return;

    setIsLoadingMy(true);
    try {
      const params: any = {
        page: currentPageMy,
        page_size: pageSizeMy,
        owner: userId,
      };

      if (filtersMy.sortBy && filtersMy.sortBy !== 'all') {
        params.sort_by = filtersMy.sortBy;
      }
      if (filtersMy.search) params.search = filtersMy.search;
      if (filtersMy.type !== 'all') params.type = filtersMy.type;

      const response = await clientRevshareService.getOffers(params);

      if (response && response.objects && Array.isArray(response.objects)) {
        const normalized = response.objects.map((item) => normalizeOffer(item));
        setMyOffers(normalized);
        setTotalCountMy(response.count || 0);
      } else {
        setMyOffers([]);
        setTotalCountMy(0);
      }
    } catch (error) {
      console.error('Error loading my revshare offers:', error);
      showError(t('revshare.errors.loadFailed'));
      setMyOffers([]);
      setTotalCountMy(0);
    } finally {
      setIsLoadingMy(false);
    }
  }, [currentPageMy, pageSizeMy, filtersMy, userId, t]);

  // Load Other Offers (with not_owner param)
  const loadOtherOffers = useCallback(async () => {
    if (!userId) return;

    setIsLoadingOther(true);
    try {
      const params: any = {
        page: currentPageOther,
        page_size: pageSizeOther,
        not_owner: userId,
      };

      if (filtersOther.sortBy && filtersOther.sortBy !== 'all') {
        params.sort_by = filtersOther.sortBy;
      }
      if (filtersOther.search) params.search = filtersOther.search;
      if (filtersOther.type !== 'all') params.type = filtersOther.type;

      const response = await clientRevshareService.getOffers(params);

      if (response && response.objects && Array.isArray(response.objects)) {
        const normalized = response.objects.map((item) => normalizeOffer(item));
        setOtherOffers(normalized);
        setTotalCountOther(response.count || 0);
      } else {
        setOtherOffers([]);
        setTotalCountOther(0);
      }
    } catch (error) {
      console.error('Error loading other revshare offers:', error);
      showError(t('revshare.errors.loadFailed'));
      setOtherOffers([]);
      setTotalCountOther(0);
    } finally {
      setIsLoadingOther(false);
    }
  }, [currentPageOther, pageSizeOther, filtersOther, userId, t]);

  useEffect(() => {
    loadMyOffers();
  }, [loadMyOffers]);

  useEffect(() => {
    loadOtherOffers();
  }, [loadOtherOffers]);

  const handlePageChangeMy = useCallback((page: number) => {
    setCurrentPageMy(page);
  }, []);

  const handlePageSizeChangeMy = useCallback((newPageSize: number) => {
    setPageSizeMy(newPageSize);
    setCurrentPageMy(1);
  }, []);

  const handlePageChangeOther = useCallback((page: number) => {
    setCurrentPageOther(page);
  }, []);

  const handlePageSizeChangeOther = useCallback((newPageSize: number) => {
    setPageSizeOther(newPageSize);
    setCurrentPageOther(1);
  }, []);

  const handleOpenOffer = useCallback(
    (offer: RevshareOffer) => {
      if (!hasOnlyFansAccounts) {
        setShowAccountModal(true);
        return;
      }

      if (!hasRequiredDeposit) {
        setShowDepositModal(true);
        return;
      }

      navigate(`/client/accounts/${offer.of_user_id}/offer`);
    },
    [hasOnlyFansAccounts, hasRequiredDeposit, navigate]
  );

  const handleDepositRedirect = useCallback(() => {
    setShowDepositModal(false);
    navigate('/client/account/wallet');
  }, [navigate]);

  const handleAccountRedirect = useCallback(() => {
    setShowAccountModal(false);
    navigate('/client/accounts/create');
  }, [navigate]);

  const handleFilterChangeMy = useCallback((key: keyof OffersFilters, value: string) => {
    setFiltersMy((prev) => ({ ...prev, [key]: value }));
    setCurrentPageMy(1);
  }, []);

  const handleResetFiltersMy = useCallback(() => {
    setFiltersMy({ ...DEFAULT_FILTERS });
    setLocalSearchMy(DEFAULT_FILTERS.search);
    setCurrentPageMy(1);
  }, []);

  const handleFilterChangeOther = useCallback((key: keyof OffersFilters, value: string) => {
    setFiltersOther((prev) => ({ ...prev, [key]: value }));
    setCurrentPageOther(1);
  }, []);

  const handleResetFiltersOther = useCallback(() => {
    setFiltersOther({ ...DEFAULT_FILTERS });
    setLocalSearchOther(DEFAULT_FILTERS.search);
    setCurrentPageOther(1);
  }, []);

  return (
    <div className="flex flex-col min-h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('revshare.offers.title')}
        </Typography>
      </div>

      {!hasRequiredDeposit && !isLoadingMy && totalCountMy === 0 && (
        <div className="px-6 pb-4">
          <DepositWarningBanner
            minimumDeposit={MINIMUM_DEPOSIT}
            onDepositClick={handleDepositRedirect}
            t={t}
          />
        </div>
      )}

      {/* My Offers Section */}
      <div className="px-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900 mb-4">
          {t('revshare.myOffers') || 'Мои оффера'}
        </Typography>

        <FiltersSection
          filters={filtersMy}
          localSearch={localSearchMy}
          hasActiveFilters={hasActiveFiltersMy}
          onSearchChange={setLocalSearchMy}
          onFilterChange={handleFilterChangeMy}
          onResetFilters={handleResetFiltersMy}
        />

        <div className="min-h-[12.5rem]">
          <OffersTable
            offers={myOffers}
            isLoading={isLoadingMy}
            totalCount={totalCountMy}
            currentPage={currentPageMy}
            pageSize={pageSizeMy}
            onPageChange={handlePageChangeMy}
            onPageSizeChange={handlePageSizeChangeMy}
            onOpenOffer={handleOpenOffer}
          />
        </div>
      </div>

      {/* Other Offers Section */}
      <div className="px-4 md:px-6 pb-6 mt-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900 mb-4">
          {t('revshare.otherOffers') || 'Другие офферы'}
        </Typography>

        <FiltersSection
          filters={filtersOther}
          localSearch={localSearchOther}
          hasActiveFilters={hasActiveFiltersOther}
          onSearchChange={setLocalSearchOther}
          onFilterChange={handleFilterChangeOther}
          onResetFilters={handleResetFiltersOther}
        />

        <OffersTable
          offers={otherOffers}
          isLoading={isLoadingOther}
          totalCount={totalCountOther}
          currentPage={currentPageOther}
          pageSize={pageSizeOther}
          onPageChange={handlePageChangeOther}
          onPageSizeChange={handlePageSizeChangeOther}
          onOpenOffer={handleOpenOffer}
          showActions={false}
          showStatus={false}
        />
      </div>

      <ResponsiveModal isOpen={showAccountModal} onClose={() => setShowAccountModal(false)}>
        <ModalContent className="md:w-[25rem]">
          <ModalHeader className="flex items-center gap-2">
            <h3 className="text-lg font-medium">{t('revshare.modals.addAccount.title')}</h3>
          </ModalHeader>
          <ModalBody className="pt-3 px-4 md:px-6 pb-6">
            <Typography variant="body2">{t('revshare.modals.addAccount.description')}</Typography>
          </ModalBody>
          <ModalFooter>
            <CustomHeroButton
              className="!text-auth-strength-weak !bg-auth-strength-weak/20"
              onPress={() => setShowAccountModal(false)}
              title={t('revshare.modals.close')}
            />
            <CustomHeroButton
              onPress={handleAccountRedirect}
              title={t('revshare.modals.addAccount.action')}
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>

      <ResponsiveModal isOpen={showDepositModal} onClose={() => setShowDepositModal(false)}>
        <ModalContent className="md:w-[25rem]">
          <ModalHeader className="flex items-center gap-2">
            <h3 className="text-lg font-medium">{t('revshare.modals.deposit.title')}</h3>
          </ModalHeader>
          <ModalBody className="pt-3 px-4 md:px-6 pb-6">
            <Typography variant="body2">
              {t('revshare.modals.deposit.description', { amount: MINIMUM_DEPOSIT })}
            </Typography>
          </ModalBody>
          <ModalFooter>
            <CustomHeroButton
              className="!text-auth-strength-weak !bg-auth-strength-weak/20"
              onPress={() => setShowDepositModal(false)}
              title={t('revshare.modals.close')}
            />
            <CustomHeroButton
              onPress={handleDepositRedirect}
              title={t('revshare.modals.deposit.action')}
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
};

export default RevshareOffersPage;
